import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ImagePlus, MessageSquareHeart, Star, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";

const Stars = ({ value, size = "h-4 w-4" }) => (
  <span className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`${size} ${n <= value ? "fill-gold text-gold" : "text-slate-300"}`} />
    ))}
  </span>
);

const inputCls =
  "h-12 w-full rounded-xl border border-slate-200 bg-paper px-4 text-sm font-medium text-ink outline-none transition-colors duration-200 focus:border-royal";

export default function ReviewsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(null);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [imagePath, setImagePath] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [uploading, setUploading] = useState(false);

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/reviews/upload", fd);
      setImagePath(data.path);
      setImagePreview(`${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const load = () => {
    api.get("/reviews").then((r) => setData(r.data)).catch(() => setData({ reviews: [], count: 0, average: 0 }));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/reviews/mine").then((r) => {
      const rv = r.data.review;
      if (rv) {
        setMine(rv);
        setRating(rv.rating);
        setTitle(rv.title);
        setBody(rv.body);
        if (rv.image) {
          setImagePath(rv.image);
          setImagePreview(`${process.env.REACT_APP_BACKEND_URL}/api/files/${rv.image}`);
        }
      }
    }).catch(() => {});
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (body.trim().length < 10) {
      return toast.error("Tell us a little more — at least 10 characters");
    }
    setBusy(true);
    try {
      await api.post("/reviews", { rating, title: title.trim(), body: body.trim(), image: imagePath });
      toast.success(mine ? "Your review is updated. Thank you!" : "Review posted — thank you!");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="bg-paper pb-28" data-testid="reviews-page">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-8 lg:px-16">
          <Reveal>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-royal">
              Wall of totes
            </p>
            <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-5xl">
              What Central Oregon{" "}
              <span className="font-serif font-normal italic text-royal">says.</span>
            </h1>
            {data && data.count > 0 && (
              <p className="mt-4 flex items-center gap-2.5 text-sm font-semibold text-ink" data-testid="reviews-summary">
                <Stars value={Math.round(data.average)} />
                {data.average} average · {data.count} review{data.count === 1 ? "" : "s"}
              </p>
            )}
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pt-12 sm:px-8 lg:px-16">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal delay={0.05}>
              {user ? (
                <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-7 shadow-xl shadow-slate-900/5 lg:sticky lg:top-28" data-testid="review-form">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-royal">
                    {mine ? "Update your review" : "Leave a review"}
                  </p>
                  <h2 className="mt-2 font-display text-xl font-bold text-ink">
                    How was your move, {user.name?.split(" ")[0]}?
                  </h2>
                  <div className="mt-5">
                    <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Your rating
                    </p>
                    <div className="flex items-center gap-1.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setRating(n)}
                          data-testid={`review-rating-${n}`}
                          aria-label={`${n} star${n === 1 ? "" : "s"}`}
                          className="transition-transform duration-150 hover:scale-110"
                        >
                          <Star className={`h-7 w-7 ${n <= rating ? "fill-gold text-gold" : "text-slate-300"}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Headline (optional)"
                    maxLength={80}
                    data-testid="review-title-input"
                    className={`${inputCls} mt-5`}
                  />
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Tell neighbors about the delivery, the totes, the pickup…"
                    rows={4}
                    maxLength={600}
                    data-testid="review-body-input"
                    className={`${inputCls} mt-3 h-auto resize-none py-3`}
                  />
                  <div className="mt-3">
                    {imagePreview ? (
                      <div className="relative">
                        <img src={imagePreview} alt="Your review upload" className="h-32 w-full rounded-xl border border-slate-200 object-cover" data-testid="review-image-preview" />
                        <button
                          type="button"
                          onClick={() => { setImagePath(""); setImagePreview(""); }}
                          aria-label="Remove photo"
                          data-testid="review-image-remove"
                          className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-white/90 text-slate-600 shadow transition-colors duration-200 hover:text-red-600"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-paper px-4 py-4 text-xs font-semibold text-slate-500 transition-colors duration-200 hover:border-royal hover:text-royal" data-testid="review-image-upload">
                        <ImagePlus className="h-4 w-4" />
                        {uploading ? "Uploading photo…" : "Add a photo of your move (optional)"}
                        <input type="file" accept="image/*" className="hidden" disabled={uploading}
                          onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="review-image-input" />
                      </label>
                    )}
                  </div>
                  <p className="mt-2 text-[11px] text-slate-400">
                    Posted as your abbreviated name (e.g. Jordan M.) — never your full name or email.
                  </p>
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="review-submit-button"
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-royal py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal-dark hover:shadow-lg hover:shadow-royal/25 disabled:opacity-60"
                  >
                    <MessageSquareHeart className="h-4 w-4" />
                    {busy ? "Posting…" : mine ? "Update my review" : "Post my review"}
                  </button>
                </form>
              ) : (
                <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-xl shadow-slate-900/5 lg:sticky lg:top-28" data-testid="reviews-signin-cta">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-royal-light">
                    <UserRound className="h-6 w-6 text-royal" />
                  </span>
                  <h2 className="mt-4 font-display text-xl font-bold text-ink">Rented from us?</h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Sign back in anytime to leave a review — it takes less than a minute and
                    helps your neighbors move smarter.
                  </p>
                  <Link
                    to="/auth"
                    state={{ from: "/reviews" }}
                    data-testid="reviews-signin-button"
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-royal py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal-dark hover:shadow-lg hover:shadow-royal/25"
                  >
                    Sign in to leave a review
                  </Link>
                </div>
              )}
              <div className="relative mt-8">
                <div className="absolute -inset-2.5 rounded-[1.75rem] border border-royal/20" aria-hidden />
                <img
                  src="/brand/owners-totes.png"
                  alt="The owners of Oregon Tote Rentals delivering a stack of blue totes"
                  className="relative w-full rounded-3xl object-cover shadow-xl shadow-slate-900/10"
                  data-testid="reviews-owners-photo"
                />
                <span className="absolute -bottom-4 left-5 rounded-full bg-royal px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-white shadow-lg shadow-royal/30">
                  The owner dropping off a fresh stack
                </span>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <div className="space-y-4">
              {data === null && <div className="h-32 animate-pulse rounded-2xl bg-slate-200/60" />}
              {data !== null && data.reviews.length === 0 && (
                <p className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-sm text-slate-500">
                  No reviews yet — be the first to share your move.
                </p>
              )}
              {data?.reviews.map((r, i) => (
                <Reveal key={r.id} delay={Math.min(i * 0.05, 0.3)}>
                  <article
                    className="rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:border-royal/40 hover:shadow-lg hover:shadow-royal/5"
                    data-testid={`review-card-${r.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <Stars value={r.rating} />
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
                        {r.created_at ? fmtDate(r.created_at.slice(0, 10)) : ""}
                      </span>
                    </div>
                    {r.title && <h3 className="mt-3 font-display text-base font-bold text-ink">{r.title}</h3>}
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{r.body}</p>
                    {r.image && (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.image}`}
                        alt={`${r.name}'s move`}
                        loading="lazy"
                        className="mt-4 max-h-64 w-full rounded-xl border border-slate-100 object-cover"
                        data-testid={`review-image-${r.id}`}
                      />
                    )}
                    <p className="mt-3 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-royal" data-testid={`review-name-${r.id}`}>
                      — {r.name}
                    </p>
                    {r.repeat_customer && (
                      <span
                        data-testid={`repeat-badge-${r.id}`}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-gold/15 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-amber-700"
                      >
                        <Star className="h-3 w-3 fill-gold text-gold" /> Repeat customer
                      </span>
                    )}
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
